import { FC, useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { HTTP_STATUSES } from "@miplatform/constants";
import {
  CalendarEventType,
  ITransferListItem,
  PlatformModules,
} from "@miplatform/types";
import { simpleSort } from "@miplatform/utils";
import { TransferList } from "@mibuildweb/components";
import {
  useInspectionTemplates,
  useOrderTemplates,
  useWorkOrderTemplates,
} from "@mibuildweb/hooks";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { CALENDAR_PAGE_CONFIG } from "../../ConfigCalendarSetList.constants";
import { ICalendarSetData } from "../../ConfigCalendarSetList.types";

export const CalendarAttachWorkTemplateBaseView: FC<
  CalendarAttachWorkTemplateBaseViewProps
> = ({
  form,
  extraTabProps: {
    isReadOnly,
    module,
    eventType = CalendarEventType.Order,
  } = {},
}) => {
  const { setValue, getValues, watch } = form;
  const workTemplateBases = watch("workTemplateBases");

  const { orderTemplates, getOrderTemplates, setOrderTemplates } =
    useOrderTemplates();
  const { workOrderTemplates, getWorkOrderTemplates, setWorkOrderTemplates } =
    useWorkOrderTemplates();
  const { inspectionTemplates, getInspectionTemplates } =
    useInspectionTemplates();

  const allWorkTemplateBases = useMemo(() => {
    if (eventType === CalendarEventType.WorkOrder) {
      return workOrderTemplates;
    }

    if (eventType === CalendarEventType.Inspection) {
      return inspectionTemplates;
    }

    return orderTemplates;
  }, [orderTemplates, workOrderTemplates, inspectionTemplates, eventType]);

  const getData = async () => {
    if (eventType === CalendarEventType.WorkOrder) {
      const response = await getWorkOrderTemplates({
        ...(module && { filters: { module: { eq: module } } }),
      });
      if (response.status === HTTP_STATUSES.OK) {
        setWorkOrderTemplates(response.data);
      }
      return;
    }

    if (eventType === CalendarEventType.Inspection) {
      getInspectionTemplates({
        ...(module && { filters: { module: { eq: module } } }),
      });
      return;
    }

    const response = await getOrderTemplates({
      ...(module && { filters: { module: { eq: module } } }),
    });
    if (response.status === HTTP_STATUSES.OK) {
      setOrderTemplates(response.data);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const availableWorkTemplateBases = useMemo(
    () =>
      allWorkTemplateBases
        .sort((a, b) => simpleSort(a.name, b.name))
        .map((i) => ({
          key: i.id,
          title: i.name,
        })) as ITransferListItem[],
    [allWorkTemplateBases]
  );

  const selectedWorkTemplateBases = useMemo(
    () =>
      workTemplateBases
        .sort((a, b) => simpleSort(a.name, b.name))
        .map((i) => ({ key: i.id, title: i.name })) as ITransferListItem[],
    [workTemplateBases]
  );

  const handleAddItem = (key: string) => {
    const selectedWorkTemplateBase = allWorkTemplateBases.find(
      (d) => d.id === key
    );
    if (!selectedWorkTemplateBase) {
      return;
    }
    const updatedWorkTemplateBases = [
      ...workTemplateBases,
      selectedWorkTemplateBase,
    ];
    setValue("workTemplateBases", updatedWorkTemplateBases, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleRemoveItem = (key: string) => {
    const updatedWorkTemplateBases = workTemplateBases.filter(
      (d) => d.id !== key
    );
    setValue("workTemplateBases", updatedWorkTemplateBases, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (workTemplateBases.length === 0) {
      setValue("workCapacities", {});
      setValue("schedulerUsers", []);
      setValue("assignableUsers", []);
      return;
    }

    const workCapacities = getValues("workCapacities");
    Object.values(workCapacities).forEach((workTemplateBaseCapacity) => {
      Object.keys(workTemplateBaseCapacity).forEach((templateId) => {
        if (!workTemplateBases.some(({ id }) => id === templateId)) {
          delete workTemplateBaseCapacity[templateId];
        }
      });
    });
    setValue("workCapacities", workCapacities);
  }, [workTemplateBases]);

  return (
    <ConfigAccordionHeader
      title={`Attach ${CALENDAR_PAGE_CONFIG[eventType].entityNamePlural}`}
    >
      <TransferList
        showAvailableItemsSearch
        availableItems={availableWorkTemplateBases}
        selectedItems={selectedWorkTemplateBases}
        onAddItem={handleAddItem}
        onRemoveItem={handleRemoveItem}
        availableTitle={`${CALENDAR_PAGE_CONFIG[eventType].entityNamePlural} List`}
        selectedTitle={`Selected ${CALENDAR_PAGE_CONFIG[eventType].entityNamePlural}`}
        emptyAvailableListMessage="There are no available items to select."
        emptySelectedListMessage={`Easily add ${CALENDAR_PAGE_CONFIG[
          eventType
        ].entityName.toLowerCase()}s to this calendar from the list on your left`}
        disabled={isReadOnly}
      />
    </ConfigAccordionHeader>
  );
};

interface CalendarAttachWorkTemplateBaseViewProps {
  form: UseFormReturn<ICalendarSetData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    module: PlatformModules;
    eventType: CalendarEventType;
  };
}
